import {
  Box,
  Text,
  Table,
  Tbody,
  Tr,
  Td,
  Center,
  Divider,
} from '@chakra-ui/react';
import {
  useInvestmentAudit,
  useTechnicalAudit,
} from '@src/modules/performance/context/reports-api-hooks';
import IndicatorLegend from '@src/modules/performance/components/IndicatorLegend';
import { coinvestorAssuranceFourGradeLegend } from '@src/modules/performance/shared/constants';
import { useRecoilValue } from 'recoil';
import { selectedDatabaseIdState } from '@transport-insights/uikit';
import ReportSection from './ReportSection';
import TrafficLight from '../indicators/TrafficLight';
import { mapValueToColourRange } from '../../../../shared/helpers';

const informationSource = {
  title: 'Audit and Assurance',
  content: [
    {
      title: 'Procedural Audit',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      Results of the latest procedural audit report for the five common subject areas:
      <br />
      Contract management, Financial management, Procurement procedures, Professional services, and progress on Previous audit.
    </Text>
    <Text fontSize="inherit" mb={2}>
      Result colour grading is as follows:
      <br />
      <IndicatorLegend items={coinvestorAssuranceFourGradeLegend} />
    </Text>
    <Text fontSize="inherit">
      Not Available means the last audit was considered out of date (before July 2015) and or the audit result was not available.
      <br />
      In some cases, the audit results for reports dated between Oct-15 and Jul-16 were assessed based on three grades, as shown above, and in the RCA report section key
    </Text>
  </>,
      source: null,
    },
    {
      title: 'Technical Audit',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      Results of the latest procedural audit report for the five common subject areas:
      <br />
      Activity management planning, Data quality, Procurement procedures, Network condition and management, Road safety, and progress on Previous audit.
    </Text>
    <Text fontSize="inherit" mb={2}>
      Result colour grading as per the Procedural audit.
      <br />
      <IndicatorLegend items={coinvestorAssuranceFourGradeLegend} />
    </Text>
  </>,
      source: <Text fontSize="inherit">Sourced from NZTA Audit and Assurance, Corporate Services</Text>,
    },
  ],
};

function CoInvestorAssuranceAudit() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const isRegionOrNational = rcaId >= 1000;
  const { data: investmentAudit, isLoading: isloadingInvestment } = useInvestmentAudit();
  const { data: technicalAudit, isLoading: isloadingTechnical } = useTechnicalAudit();
  const isLoading = isloadingInvestment || isloadingTechnical;

  // Procedural Audit
  const distinctInvestmentAuditCategories = [...new Set(investmentAudit?.map((a) => a.ColumnTitle))];
  const investmentAuditGroupedByRca = investmentAudit?.reduce((acc, item) => {
    const council = item.Database_Name;

    if (!acc[council]) {
      acc[council] = {};
    }

    if (!acc[council][item.ColumnTitle]) {
      acc[council][item.ColumnTitle] = {};
    }

    acc[council][item.ColumnTitle] = item.ColourScore;
    acc[council].auditDate = item.date;
    return acc;
  }, {}) || undefined;

  // Technical Audit
  const distinctTechnicalAuditCategories = [...new Set(technicalAudit?.map((a) => a.ColumnTitle))];
  const technicalAuditGroupedByRca = technicalAudit?.reduce((acc, item) => {
    const council = item.Database_Name;
    if (!acc[council]) {
      acc[council] = {};
    }
    if (!acc[council][item.ColumnTitle]) {
      acc[council][item.ColumnTitle] = {};
    }
    acc[council][item.ColumnTitle] = item.ColourScore;
    acc[council].auditDate = item.date;
    return acc;
  }, {}) || undefined;

  return (
    <ReportSection
      title="Audit and Assurance"
      informationSource={informationSource}
      isLoading={isLoading}
    >
      <Box width="100%">
        {investmentAudit?.length > 0 && (
        <Box mb={4}>
          <Text fontSize="md" fontWeight="bold" mb={2}>
            Procedural Audit
          </Text>
          <Table variant="simple" size="sm" width="100%">
            <Tbody>
              <Tr>
                {isRegionOrNational && <Td textAlign="left">&nbsp;</Td>}
                {distinctInvestmentAuditCategories.map((cat) => (
                  <Td key={cat} width="15%" valign="top">
                    <Text align="center" fontSize="sm">{cat}</Text>
                  </Td>
                ))}
                <Td valign="top"><Text fontSize="sm">Audit Date</Text></Td>
              </Tr>
              {Object.entries(investmentAuditGroupedByRca)?.map(([rca, results]) => (
                <Tr key={rca}>
                  {isRegionOrNational && <Td fontWeight="bold"><Text align="left" fontSize="sm">{rca}</Text></Td>}
                  {distinctInvestmentAuditCategories.map((cat) => (
                    <Td key={cat} textAlign="center">
                      <Center>
                        <TrafficLight
                          size={8}
                          colour={mapValueToColourRange(results[cat], 'coassurance')}
                        />
                      </Center>
                    </Td>
                  ))}
                  <Td>
                    {results.auditDate ? (
                      <Text fontSize="sm">
                        {results.auditDate}
                      </Text>
                    ) : '–'}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <Text mt={2} mb={4} fontSize="xs" className="report-source-text">
            Source: NZTA, Audit and Assurance, Procedural Audit Report
          </Text>
          <IndicatorLegend items={coinvestorAssuranceFourGradeLegend} />
        </Box>
        )}
        <Divider my={8} />
        {technicalAudit?.length > 0 && (
          <Box>
            <Text fontSize="md" fontWeight="bold" mb={2}>
              Technical Audit
            </Text>
            <Table variant="simple" size="sm" width="100%">
              <Tbody>
                <Tr>
                  {isRegionOrNational && <Td textAlign="left">&nbsp;</Td>}
                  {distinctTechnicalAuditCategories.map((cat) => (
                    <Td key={cat} width="15%" valign="top">
                      <Text align="center" fontSize="sm">{cat}</Text>
                    </Td>
                  ))}
                  <Td valign="top"><Text fontSize="sm">Audit Date</Text></Td>
                </Tr>
                {Object.entries(technicalAuditGroupedByRca)?.map(([rca, results]) => (
                  <Tr key={rca}>
                    {isRegionOrNational && <Td fontWeight="bold"><Text align="left" fontSize="sm">{rca}</Text></Td>}
                    {distinctTechnicalAuditCategories.map((cat) => (
                      <Td key={cat} textAlign="center">
                        <Center>
                          <TrafficLight
                            size={8}
                            colour={mapValueToColourRange(results[cat], 'coassurance')}
                          />
                        </Center>
                      </Td>
                    ))}
                    <Td>
                      {results.auditDate ? (
                        <Text fontSize="sm">
                          {results.auditDate}
                        </Text>
                      ) : '–'}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Text mt={2} mb={4} fontSize="xs" className="report-source-text">
              Source: NZTA, Audit and Assurance, Technical Audit Report
            </Text>
          </Box>
        )}
        <IndicatorLegend items={coinvestorAssuranceFourGradeLegend} />
      </Box>
    </ReportSection>
  );
}

export default CoInvestorAssuranceAudit;
